import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions';
import { useUserConfig } from '../../contexts/UserConfigContext';
import AdminLayout from '../../layouts/AdminLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import notify from '../../utils/IANotification';
import { MdArrowBack, MdSave, MdDelete } from 'react-icons/md';
import { fetch_report_by_id, create_report, update_report, delete_report, fetch_all_locations } from '../../api/locationsAPI';
import { fetch_all_users_admin } from '../../api/usersAPI';
import { fetch_config } from '../../api/configAPI';

export default function LocationReportFormPage() {
    const navigate = useNavigate();
    useFetchAuthorisation("user");
    const userData = useFetchUserData();

    const report_id = new URLSearchParams(window.location.search).get("report_id") || "new";
    const initialLocationId = new URLSearchParams(window.location.search).get("location_id") || "";

    const [locations, setLocations] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [reportTypes, setReportTypes] = useState(["Aircon", "Electrical", "Plumbing", "Furniture", "Cleanliness", "Audio/Visual", "Other"]);

    const [reportData, setReportData] = useState({
        location_id: initialLocationId,
        report_type: "",
        title: "",
        description: "",
        priority: "medium",
        status: "open",
        assigned_to_user_id: "",
        resolution_notes: "",
    });

    const loadDataAndConfig = async () => {
        // Fetch locations and users
        const [locRes, usersRes, cfgRes] = await Promise.all([
            fetch_all_locations(),
            fetch_all_users_admin(),
            fetch_config(),
        ]);

        if (locRes?.status === 200) {
            setLocations(locRes.data || []);
            if (!initialLocationId && locRes.data?.length > 0 && report_id === "new") {
                setReportData(prev => ({ ...prev, location_id: locRes.data[0].id }));
            }
        }

        if (usersRes?.status === 200) {
            setUsersList(usersRes.data || []);
        }

        let availableTypes = ["Aircon", "Electrical", "Plumbing", "Furniture", "Cleanliness", "Audio/Visual", "Other"];
        if (cfgRes?.status === 200 && cfgRes.data?.general?.maintenance_report_types?.length > 0) {
            availableTypes = cfgRes.data.general.maintenance_report_types;
        }
        setReportTypes(availableTypes);

        if (report_id === "new") {
            setReportData(prev => ({ ...prev, report_type: availableTypes[0] || "Other" }));
        }
    };

    const loadReportData = async () => {
        if (report_id === "new") return;
        const res = await fetch_report_by_id(report_id);
        if (res?.status === 200 && res.data) {
            setReportData(res.data);
        }
    };

    useEffect(() => {
        loadDataAndConfig();
        loadReportData();
    }, [report_id]);

    const handleSave = async () => {
        if (!reportData.location_id || !reportData.title || !reportData.report_type) {
            return notify({ type: "error", message: "Please fill in location, title, and report type" });
        }

        if (report_id === "new") {
            const payload = {
                ...reportData,
                reported_by_user_id: userData?.id,
            };
            const res = await create_report(payload);
            if (res?.status === 200) {
                notify({ type: "success", message: "Maintenance report submitted successfully" });
                navigate('/admin/locations/reports');
            } else {
                notify({ type: "error", message: res?.message || "Failed to submit report" });
            }
        } else {
            const res = await update_report(report_id, reportData);
            if (res?.status === 200) {
                notify({ type: "success", message: "Maintenance report updated successfully" });
                loadReportData();
            } else {
                notify({ type: "error", message: res?.message || "Failed to update report" });
            }
        }
    };

    const handleDelete = async () => {
        if (report_id === "new") return;
        const res = await delete_report(report_id);
        if (res?.status === 200) {
            notify({ type: "success", message: "Report deleted successfully" });
            navigate('/admin/locations/reports');
        } else {
            notify({ type: "error", message: res?.message || "Failed to delete report" });
        }
    };

    return (
        <AdminLayout
            navSelected={"Maintenance Issues"}
            headerText={report_id === "new" ? "New Report" : "Manage Report"}
            toolbar={
                <>
                    <div className="flex items-center gap-3">
                        <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/locations/reports')}>
                            Back
                        </Button>
                        <h1 className="text-xl font-bold text-gray-900">
                            {report_id === "new" ? "Report Maintenance Issue" : reportData.title || "Report Details"}
                        </h1>
                    </div>
                    <div className="flex gap-2">
                        {report_id !== "new" && (
                            <Button variant="danger" icon={MdDelete} onClick={handleDelete}>
                                Delete
                            </Button>
                        )}
                        <Button variant="primary" icon={MdSave} onClick={handleSave}>
                            {report_id === "new" ? "Submit Issue" : "Save Updates"}
                        </Button>
                    </div>
                </>
            }
        >
            <Card title="Maintenance Issue Details" subtitle="Location, issue type category, priority level, and assignment.">
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="input-container">
                            <label className="input-label font-semibold">Location / Facility</label>
                            <select
                                className="select-input"
                                value={reportData.location_id || ""}
                                onChange={(e) => setReportData({ ...reportData, location_id: e.target.value })}
                            >
                                {locations.map(loc => (
                                    <option key={loc.id} value={loc.id}>
                                        {loc.name} ({loc.location_type})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="input-container">
                            <label className="input-label font-semibold">Type of Report (Dynamic)</label>
                            <select
                                className="select-input"
                                value={reportData.report_type || ""}
                                onChange={(e) => setReportData({ ...reportData, report_type: e.target.value })}
                            >
                                {reportTypes.map((type, idx) => (
                                    <option key={idx} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="input-container">
                        <label className="input-label font-semibold">Issue Title</label>
                        <input
                            type="text"
                            className="text-input"
                            placeholder="e.g. Main Sanctuary AC blowing warm air"
                            value={reportData.title || ""}
                            onChange={(e) => setReportData({ ...reportData, title: e.target.value })}
                        />
                    </div>

                    <div className="input-container">
                        <label className="input-label font-semibold">Issue Description</label>
                        <textarea
                            className="textarea-input h-32"
                            placeholder="Provide full context regarding the defect or required maintenance..."
                            value={reportData.description || ""}
                            onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-gray-100">
                        <div className="input-container">
                            <label className="input-label font-semibold">Priority Level</label>
                            <select
                                className="select-input"
                                value={reportData.priority || "medium"}
                                onChange={(e) => setReportData({ ...reportData, priority: e.target.value })}
                            >
                                <option value="low">Low Priority</option>
                                <option value="medium">Medium Priority</option>
                                <option value="high">High Priority</option>
                                <option value="urgent">Urgent Priority</option>
                            </select>
                        </div>

                        <div className="input-container">
                            <label className="input-label font-semibold">Report Status</label>
                            <select
                                className="select-input"
                                value={reportData.status || "open"}
                                onChange={(e) => setReportData({ ...reportData, status: e.target.value })}
                            >
                                <option value="open">Open</option>
                                <option value="in_progress">In Progress</option>
                                <option value="resolved">Resolved</option>
                                <option value="closed">Closed</option>
                            </select>
                        </div>

                        <div className="input-container">
                            <label className="input-label font-semibold">Assign to User / Staff</label>
                            <select
                                className="select-input"
                                value={reportData.assigned_to_user_id || ""}
                                onChange={(e) => setReportData({ ...reportData, assigned_to_user_id: e.target.value })}
                            >
                                <option value="">-- Unassigned --</option>
                                {usersList.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.first_name} {u.last_name} ({u.email || u.account_type})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {report_id !== "new" && (
                        <div className="input-container pt-4 border-t border-gray-100">
                            <label className="input-label font-semibold">Resolution Notes &amp; Action Taken</label>
                            <textarea
                                className="textarea-input h-24"
                                placeholder="Describe fix or contractor repair details..."
                                value={reportData.resolution_notes || ""}
                                onChange={(e) => setReportData({ ...reportData, resolution_notes: e.target.value })}
                            />
                        </div>
                    )}
                </div>
            </Card>
        </AdminLayout>
    );
}
